import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { patientService } from '../../patient/services/patient-service';
import { userService } from '../../admin/services/admin-user-service';
import { ApiBatchLimits } from '../../shared/enums';
import { savePatientChampion } from '../../patient/state/patient-thunk';
import { setSelectedEncounter } from '../../patient/state/patient-slice';
import { AppDispatch } from '../../shared/state/store';
import { getPatientsAndChampionsList, NEW_PAYOR_ID } from '../hub-utils';
import { PayorWithUserStatus } from '../interfaces/payment-hub-interfaces';
import { getStateByAbbrev } from '../../shared/misc/us-states';

export interface GuarantorAddress {
  streetAddress1: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  birthDate?: string;
  relationshipToPatient?: string;
}

export interface CreateChampionParams {
  firstName: string;
  lastName: string;
  email: string;
  isGuarantor: boolean;
  patientId: string;
  patientEncounterId: string;
  clientId: number;
  address?: GuarantorAddress;
}

export interface CreateChampionResult {
  success: boolean;
  championId?: number;
  userId?: number;
  error?: string;
}

export interface CreateChampionDependencies {
  dispatch: AppDispatch;
  selectedPatient: PatientViewModel;
  authToken?: string;
  onPayorsListUpdate?: (payors: PayorWithUserStatus[]) => void;
}

/**
 * Create a new champion with user account.
 * This handles the full flow of:
 * 1. Creating/finding the user account holder
 * 2. Creating the champion record
 * 3. Refreshing the encounter data
 * 4. Updating the payors list
 */

const createNewChampion = async (
  params: CreateChampionParams,
  deps: CreateChampionDependencies
): Promise<CreateChampionResult> => {
  const { firstName, lastName, email, isGuarantor, patientId, patientEncounterId, clientId, address } = params;
  const { dispatch, selectedPatient, authToken, onPayorsListUpdate } = deps;

  if (!patientId || !patientEncounterId) {
    return { success: false, error: 'Patient ID and Encounter ID are required to create a champion' };
  }

  if (!firstName || !lastName || !email) {
    return { success: false, error: 'First name, last name, and email are required to create a champion' };
  }

  try {
    // Step 1: Create or get the user account holder
    const championUsersResponse = await userService.getAllUsers(
      0,
      ApiBatchLimits.users,
      { userName: email }
    );

    if (championUsersResponse.hasErrors) {
      return { success: false, error: 'Error fetching user' };
    }

    const userResponse = await userService.handleCreateOrUpdateUser({
      userEmail: email,
      clientId,
      name: { firstName, lastName },
      patientEncounterId: parseInt(patientEncounterId, 10),
      filteredUsers: championUsersResponse.entity,
    });

    const newUserId = userResponse.userId;
    if (!newUserId) {
      return { success: false, error: 'User ID is required to create champion' };
    }

    console.log('[ChampionService] User created/found for new champion:', newUserId);

    // Build primary address if provided (for guarantor)
    const primaryAddress = address && isGuarantor ? {
      streetAddress1: address.streetAddress1,
      streetAddress2: '',
      city: address.city,
      state: getStateByAbbrev(address.state) || { stateId: 0, stateName: '', stateCode: address.state },
      zipCode: address.zipCode,
      zipCode4: '',
      addressType: 'Primary',
    } : undefined;

    // Step 2: Create champion with userId
    const newChampion: any = {
      patientChampionId: 0,
      patientEncounterId: parseInt(patientEncounterId, 10),
      contact: {
        firstName,
        lastName,
        email,
        leaveVMFl: false,
        smsConsentFl: true,
        ...(address?.phone && { primPhoneNum: address.phone }),
        ...(address?.birthDate && { birthDate: address.birthDate }),
        ...(primaryAddress && { primaryAddress }),
      },
      relationshipToPatient: address?.relationshipToPatient || '',
      isGuarantor,
      phiDocumentStatus: false,
      userId: newUserId,
    };

    const championResult = await dispatch(
      savePatientChampion({
        champion: newChampion,
        patientId: parseInt(patientId, 10),
        encounterId: parseInt(patientEncounterId, 10),
      })
    ).unwrap();

    const newChampionId = championResult.patientChampionId;
    console.log('[ChampionService] New champion created:', newChampionId);

    // Step 3: Refresh the encounter data to get the updated champion list
    const iocResponse = await patientService.getPatientInstanceOfCare({
      patientId: parseInt(patientId, 10),
      encounterId: parseInt(patientEncounterId, 10),
    });

    if (!iocResponse.hasErrors && iocResponse.entity) {
      dispatch(setSelectedEncounter(iocResponse.entity));
      
      // Step 4: Refresh the payors list
      if (onPayorsListUpdate) {
        const updatedPayorsList = await getPatientsAndChampionsList(
          selectedPatient,
          iocResponse.entity,
          false,
          authToken
        );
        onPayorsListUpdate(updatedPayorsList as PayorWithUserStatus[]);
      }
    }

    return { success: true, championId: newChampionId, userId: newUserId };
  } catch (error: any) {
    console.error('[ChampionService] Error creating champion:', error);
    return { success: false, error: error.message || 'Failed to create champion' };
  }
};

export interface ChampionContactUpdates {
  phone?: string;
  birthDate?: string;
  relationshipToPatient?: string;
  address?: {
    streetAddress1: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export interface UpdateChampionParams {
  payorId: string;
  payorsWithStatus: PayorWithUserStatus[];
  patientId?: number;  // Needed for patient updates when patient is the guarantor
  updates: Partial<{
    isGuarantor: boolean;
    contactInfo?: ChampionContactUpdates;
  }>;
}

export interface UpdateChampionResult {
  success: boolean;
  error?: string;
}

export interface UpdateChampionDependencies {
  onPayorsListUpdate?: (updater: (prev: PayorWithUserStatus[]) => PayorWithUserStatus[]) => void;
  onSuccess?: () => void;
}

const updateChampion = async (
  params: UpdateChampionParams,
  deps: UpdateChampionDependencies = {}
): Promise<UpdateChampionResult> => {
  const { payorId, payorsWithStatus, patientId, updates } = params;
  const { onPayorsListUpdate, onSuccess } = deps;

  if (payorId === NEW_PAYOR_ID) {
    return { success: true };
  }
  
  const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === payorId);
  if (!selectedPayor) {
    return { success: false, error: 'Invalid payor selection' };
  }

  // Handle patient type - update patient's contact record
  if (selectedPayor.type === 'patient') {
    if (!patientId) {
      return { success: false, error: 'Patient ID is required for patient contact update' };
    }
    
    try {
      const patientUpdatePayload: any = {};
      
      if (updates.contactInfo) {
        const contactUpdates: any = {};
        
        if (updates.contactInfo.phone) {
          contactUpdates.primPhoneNum = updates.contactInfo.phone;
        }
        
        if (updates.contactInfo.birthDate) {
          contactUpdates.birthDate = updates.contactInfo.birthDate;
        }
        
        if (updates.contactInfo.address) {
          contactUpdates.primaryAddress = {
            streetAddress1: updates.contactInfo.address.streetAddress1,
            streetAddress2: '',
            city: updates.contactInfo.address.city,
            state: getStateByAbbrev(updates.contactInfo.address.state) || { 
              stateId: 0, 
              stateName: '', 
              stateCode: updates.contactInfo.address.state 
            },
            zipCode: updates.contactInfo.address.zipCode,
            zipCode4: '',
            addressType: 'Primary',
          };
        }
        
        if (Object.keys(contactUpdates).length > 0) {
          patientUpdatePayload.contact = contactUpdates;
        }
      }

      if (Object.keys(patientUpdatePayload).length === 0) {
        console.log('[ChampionService] No patient contact updates to apply');
        return { success: true };
      }

      console.log('[ChampionService] Updating patient contact with payload:', patientUpdatePayload);
      
      const updateResult = await patientService.updatePatient(
        patientUpdatePayload,
        patientId
      );
      
      if (!updateResult.hasErrors) {
        console.log('[ChampionService] Successfully updated patient contact:', patientId);
        if (onSuccess) {
          onSuccess();
        }
        return { success: true };
      } else {
        console.error('[ChampionService] Failed to update patient contact:', updateResult.errorMessage);
        return { success: false, error: updateResult.errorMessage || 'Failed to update patient contact' };
      }
    } catch (error: any) {
      console.error('[ChampionService] Error updating patient contact:', error);
      return { success: false, error: error.message || 'Error updating patient contact' };
    }
  }

  // Handle champion type - existing logic
  if (selectedPayor.type !== 'champion' || typeof selectedPayor.id !== 'number') {
    return { success: false, error: 'Invalid payor selection for champion update' };
  }
  
  try {
    const updatePayload: any = {};
    
    if (updates.isGuarantor !== undefined) {
      updatePayload.isGuarantor = updates.isGuarantor;
    }
    
    if (updates.contactInfo?.relationshipToPatient) {
      updatePayload.relationshipToPatient = updates.contactInfo.relationshipToPatient;
    }
    
    if (updates.contactInfo) {
      const contactUpdates: any = {};
      
      if (updates.contactInfo.phone) {
        contactUpdates.primPhoneNum = updates.contactInfo.phone;
      }
      
      if (updates.contactInfo.birthDate) {
        contactUpdates.birthDate = updates.contactInfo.birthDate;
      }
      
      if (updates.contactInfo.address) {
        contactUpdates.primaryAddress = {
          streetAddress1: updates.contactInfo.address.streetAddress1,
          streetAddress2: '',
          city: updates.contactInfo.address.city,
          state: getStateByAbbrev(updates.contactInfo.address.state) || { 
            stateId: 0, 
            stateName: '', 
            stateCode: updates.contactInfo.address.state 
          },
          zipCode: updates.contactInfo.address.zipCode,
          zipCode4: '',
          addressType: 'Primary',
        };
      }
      
      if (Object.keys(contactUpdates).length > 0) {
        updatePayload.contact = contactUpdates;
      }
    }

    console.log('[ChampionService] Updating champion with payload:', updatePayload);
    
    const updateResult = await patientService.updatePatientChampion(
      updatePayload,
      selectedPayor.id
    );
    
    if (!updateResult.hasErrors) {
      console.log('[ChampionService] Successfully updated champion:', selectedPayor.id, updatePayload);

      if (onPayorsListUpdate) {
        onPayorsListUpdate(prev => prev.map(p => 
          p.id === selectedPayor.id ? { ...p, isGuarantor: updates.isGuarantor ?? p.isGuarantor } : p
        ));
      }
      if (onSuccess) {
        onSuccess();
      }
      return { success: true };
    } else {
      console.error('[ChampionService] Failed to update champion:', updateResult.errorMessage);
      return { success: false, error: updateResult.errorMessage || 'Failed to update champion' };
    }
  } catch (error: any) {
    console.error('[ChampionService] Error updating champion:', error);
    return { success: false, error: error.message || 'Error updating champion' };
  }
};

export interface CreateChampionIfNeededParams {
  payorId: string;
  billingDetails: { firstName: string; lastName: string; email: string };
  isGuarantor: boolean;
  isRequired: boolean; // If true, failure should stop the flow
  patientId: string;
  patientEncounterId: string;
  clientId: number;
  address?: GuarantorAddress;
}

export interface CreateChampionIfNeededResult {
  success: boolean;
  championId?: number;
  userId?: number;
  error?: string;
}

export const createChampionIfNeeded = async (
  params: CreateChampionIfNeededParams,
  deps: CreateChampionDependencies,
  onError?: (message: string) => void
): Promise<CreateChampionIfNeededResult> => {
  const { payorId, billingDetails, isGuarantor, isRequired, patientId, patientEncounterId, clientId, address } = params;

  if (payorId !== NEW_PAYOR_ID) {
    return { success: true };
  }

  const result = await createNewChampion(
    {
      firstName: billingDetails.firstName,
      lastName: billingDetails.lastName,
      email: billingDetails.email,
      isGuarantor,
      patientId,
      patientEncounterId,
      clientId,
      address,
    },
    deps
  );

  if (!result.success) {
    if (isRequired && onError) {
      onError(result.error || 'Failed to create champion');
    } else {
      console.error('[ChampionService] Non-critical champion creation failed:', result.error);
    }
  }

  return { 
    success: result.success, 
    championId: result.championId, 
    userId: result.userId,
    error: result.error 
  };
};

export interface AddressDetails {
  phone?: string;
  birthDate?: string;
  relationshipToPatient?: string;
  streetAddress1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface UpdatePayorInfoParams {
  // Payor being made a new guarantor (checkbox checked) - will set isGuarantor: true
  newGuarantorPayorId?: string;
  // Payor whose guarantor form is visible (update contact info)
  guarantorFormPayorId?: string;
  guarantorFormAddressDetails?: AddressDetails;
  payorsWithStatus: PayorWithUserStatus[];
  patientId?: string;
  downPaymentPayorId?: string;
  downPaymentEmail?: string;
  recurringPayorId?: string;
  recurringEmail?: string;
}

export interface UpdatePayorInfoDependencies {
  onPayorsListUpdate: (updater: (prev: PayorWithUserStatus[]) => PayorWithUserStatus[]) => void;
  onResetGuarantorCheckbox: () => void;
}

/**
 * Unset any existing champion guarantor (set isGuarantor = false).
 */
const unsetExistingChampionGuarantor = async (
  payorsWithStatus: PayorWithUserStatus[],
  newGuarantorPayorId?: string
): Promise<void> => {
  const existingChampionGuarantor = payorsWithStatus.find(
    p => p.isGuarantor && p.type === 'champion' && p.id?.toString() !== newGuarantorPayorId
  );

  if (existingChampionGuarantor && typeof existingChampionGuarantor.id === 'number') {
    try {
      await patientService.updatePatientChampion(
        { isGuarantor: false },
        existingChampionGuarantor.id
      );
    } catch (error) {
    }
  }
};

/**
 * Updates champion email if it has changed from the original.
 * Only applies to existing champions (not patients or new payors).
 */
const updateChampionEmail = async (
  payorId: string,
  payorsWithStatus: PayorWithUserStatus[],
  newEmail?: string
): Promise<void> => {
  if (!newEmail || payorId === NEW_PAYOR_ID) {
    return;
  }

  const selectedPayor = payorsWithStatus.find(p => p.id?.toString() === payorId);
  if (!selectedPayor || selectedPayor.type !== 'champion' || typeof selectedPayor.id !== 'number') {
    return;
  }

  // Only update if email has changed
  if (selectedPayor.email === newEmail) {
    return;
  }

  try {
    console.log('[ChampionService] Updating champion email:', { championId: selectedPayor.id, newEmail });
    await patientService.updatePatientChampion(
      { contact: { email: newEmail } },
      selectedPayor.id
    );
  } catch (error) {
    console.error('[ChampionService] Failed to update champion email:', error);
  }
};

/**
 * Unified function to update payor info.
 * 
 * Handles:
 * 1. Setting guarantor status when "Make Guarantor" checkbox is checked (unsets previous guarantor first)
 * 2. Updating contact info when guarantor form is visible
 * 3. Updating email for existing champions when changed
 */
export const updatePayorInfo = async (
  params: UpdatePayorInfoParams,
  deps: UpdatePayorInfoDependencies
): Promise<void> => {
  const {
    newGuarantorPayorId,
    guarantorFormPayorId,
    guarantorFormAddressDetails,
    payorsWithStatus,
    patientId,
    downPaymentPayorId,
    downPaymentEmail,
    recurringPayorId,
    recurringEmail,
  } = params;
  const { onPayorsListUpdate, onResetGuarantorCheckbox } = deps;

  const guarantorPayor = guarantorFormPayorId 
    ? payorsWithStatus.find(p => p.id?.toString() === guarantorFormPayorId) 
    : null;

  console.log('[updatePayorInfo] Guarantor information to be updated:', {
    newGuarantorPayorId,
    guarantorFormPayorId,
    guarantorPayorType: guarantorPayor?.type,
    guarantorPayorName: guarantorPayor?.name,
    guarantorFormAddressDetails,
    patientId,
    downPaymentPayorId,
    downPaymentEmail,
    recurringPayorId,
    recurringEmail,
  });

  const parsedPatientId = patientId ? parseInt(patientId, 10) : undefined;

  // Set guarantor status if a payor is being made guarantor (checkbox checked)
  if (newGuarantorPayorId) {
    await unsetExistingChampionGuarantor(payorsWithStatus, newGuarantorPayorId);
    
    await updateChampion(
      {
        payorId: newGuarantorPayorId,
        payorsWithStatus,
        patientId: parsedPatientId,
        updates: { isGuarantor: true }
      },
      {
        onPayorsListUpdate,
        onSuccess: onResetGuarantorCheckbox
      }
    );
  }

  if (guarantorFormPayorId && guarantorFormAddressDetails) {
    const hasAddressData = guarantorFormAddressDetails.streetAddress1 || 
      guarantorFormAddressDetails.city || 
      guarantorFormAddressDetails.state || 
      guarantorFormAddressDetails.zipCode;
    
    await updateChampion(
      {
        payorId: guarantorFormPayorId,
        payorsWithStatus,
        patientId: parsedPatientId,
        updates: {
          contactInfo: {
            phone: guarantorFormAddressDetails.phone,
            birthDate: guarantorFormAddressDetails.birthDate,
            relationshipToPatient: guarantorFormAddressDetails.relationshipToPatient,
            address: hasAddressData ? {
              streetAddress1: guarantorFormAddressDetails.streetAddress1 || '',
              city: guarantorFormAddressDetails.city || '',
              state: guarantorFormAddressDetails.state || '',
              zipCode: guarantorFormAddressDetails.zipCode || '',
            } : undefined,
          },
        }
      },
      {}
    );
  }

  // Update champion emails if changed (only for existing champions, not patients)
  if (downPaymentPayorId && downPaymentEmail) {
    await updateChampionEmail(downPaymentPayorId, payorsWithStatus, downPaymentEmail);
  }
  if (recurringPayorId && recurringEmail) {
    await updateChampionEmail(recurringPayorId, payorsWithStatus, recurringEmail);
  }
};
